import round from 'lodash/round';
import { type MapRef } from 'react-map-gl-v7';
import WebMercatorViewport from 'viewport-mercator-project';
import AbortError from '@discover-data/stores/restaurant/utils/store/AbortError';
import { type Restaurant } from '@discover-data/gql/getters/mapRestaurants';
import haversineDistance from '@/lib/geo/haversineDistance';
import fetchRestaurants, { FetchRestaurantsParams } from './fetchRestaurants';

let abortController = new AbortController();

function abort() {
  if (!abortController.signal.aborted) {
    abortController.abort(new AbortError('Abort previous restaurant fetch before re-fetching'));
  }
}

export default async function onChange({
  centerPoint,
  queryVariables,
  mapRef,
  setLoading,
  setRestaurants,
}: {
  centerPoint: ViewState;
  queryVariables: Omit<FetchRestaurantsParams['variables']['input'], 'nearTo'>;
  mapRef: MapRef;
  setLoading: (loading: boolean) => void;
  setRestaurants: (restaurants: Restaurant[]) => void;
}) {
  if (!abortController.signal.aborted) {
    // abort previous fetch before starting new fetch
    abort();
  }

  abortController = new AbortController();

  const mapContainer = mapRef.getContainer();
  const mapWidth = mapContainer.offsetWidth;
  const mapHeight = mapContainer.offsetHeight;

  const [p1, p2] = new WebMercatorViewport({
    width: mapWidth,
    height: mapHeight,
    latitude: centerPoint.latitude,
    longitude: centerPoint.longitude,
    zoom: centerPoint.zoom,
  }).getBounds();

  // get the length of the longest side of the viewport, in meters
  let lengthM = 0;
  if (mapWidth > mapHeight) {
    lengthM = haversineDistance(
      { lat: centerPoint.latitude, lng: p1[0] },
      { lat: centerPoint.latitude, lng: p2[0] }
    );
  } else {
    lengthM = haversineDistance(
      { lat: p1[1], lng: centerPoint.longitude },
      { lat: p2[1], lng: centerPoint.longitude }
    );
  }

  const radiusKm = lengthM / 2 / 1000;
  const overscanFactor = 2.5; // a factor multiply the proximity by, to fetch some of the area outside of the viewport

  // rounding the query inputs to specific decimal places, to potentially make better use of caching (based on a fraction of the zoom level, to have better accuracy on higher zoom levels)
  const positionDecimals = Math.floor(centerPoint.zoom * 0.15);
  const proximityDecimals = Math.floor(centerPoint.zoom * 0.08);

  const results = await fetchRestaurants(
    {
      input: {
        ...queryVariables,
        nearTo: {
          lat: round(centerPoint.latitude, positionDecimals),
          lng: round(centerPoint.longitude, positionDecimals),
          proximity: round(radiusKm * overscanFactor, proximityDecimals),
        },
      },
    },
    setLoading,
    abortController.signal
  );

  if (results) {
    setRestaurants(results);
  }
}
